import { Reducer, useReducer, useContext, useRef, useEffect } from 'react';
import { Store } from './storeContext';

export type MiddlewareFn<T> = (action: any, state: T, dispatch: Dispatch<T>) => void;
export type Dispatch<T> = (action: any) => void;
export type UseStoreCallback<T> = (
  data: Partial<T> | ((state: T) => Partial<T>),
  callback?: () => void,
) => void;

export function updateStore<T>(
  dispatch: Dispatch<T>,
  data: Partial<T> | ((state: T) => Partial<T>),
  callback?: () => void,
) {
  dispatch({ type: 'UPDATE_VALUE', data });

  if (callback) {
    queueMicrotask(callback);
  }
}

export function showError<T>(dispatch: Dispatch<T>, data: ErrorType) {
  dispatch({ type: 'SHOW_ERROR', data });
}

export const useStoreContext = <T>() => {
  const { storeState, dispatch } = useContext(Store) as {
    storeState: T;
    dispatch: Dispatch<T>;
  };
  return { storeState, dispatch };
};

export const useStore = <T>(): [T, UseStoreCallback<T>] => {
  const { storeState, dispatch } = useStoreContext<T>();
  return [storeState, (data, callback) => updateStore(dispatch, data, callback)];
};

export const useReducerWithMiddleware = <T = StoreState>(
  reducer: Reducer<T, any>,
  initialState: T,
  middlewareFns: MiddlewareFn<T>[],
  afterwareFns: MiddlewareFn<T>[],
): [T, Dispatch<T>] => {
  const [state, dispatch] = useReducer<T, any>(reducer, initialState);
  const aRef = useRef<{ actions: any[] }>({ actions: [] });

  const dispatchWithMiddleware: Dispatch<T> = (action) => {
    for (const middlewareFn of middlewareFns) {
      middlewareFn(action, state, dispatch);
    }

    if (typeof action === 'function') {
      return action(state, dispatchWithMiddleware);
    }

    aRef.current.actions.push(action);
    dispatch(action);
  };

  useEffect(() => {
    if (!aRef.current.actions.length) return;
    const [nextAction] = aRef.current.actions;
    afterwareFns.forEach((afterwareFn) => afterwareFn(nextAction, state, dispatch));
    aRef.current.actions.shift();
  }, [afterwareFns, state]);

  return [state, dispatchWithMiddleware];
};
